/**
 * versona_tts.ts — the versona VOICE service (2026-09-10): a versona's
 * dialogue answers read aloud in the browser by KittenTTS (workers/tts_worker),
 * in the voice its body earns (lib/tts/versona_voice), through the shared
 * mix's DIALOGUE bus (lib/audio/runtime — behind the intro's game gate and the
 * master; it used to ride the sfx master, where it could not duck the music). One utterance at a time: a new line interrupts the old one, closing
 * the window silences it. The worker is created lazily on the first request
 * (the model is 24 MB + 14 MB of WASM, fetched once and cached by the browser)
 * and a paragraph is synthesised chunk by chunk, each chunk queued to play
 * the moment it lands — the first sentence starts while the rest is still
 * being computed.
 *
 * Setting: localStorage 'atlas:voices' ('on' | 'off', default on) and
 * 'atlas:voices-volume' (0..1, default 0.9). Diag `__tts()`.
 */
import { sfxOutput } from '../sfx.js';
import { audioRuntime } from '../audio/runtime.js';
import { DUCK_SPEECH } from '../audio/mix_math.js';
import { prepareUtterance, KITTEN_SAMPLE_RATE } from './kitten_text.js';
import { modelSpeed, VOICE_NAMES, type VoiceChoice } from './versona_voice.js';
import { interruptAllowed, ambientVerdict, PRIORITY_RANK, type SpeechPriority, type AmbientVerdict } from './speech_budget.js';

export const VOICES_KEY = 'atlas:voices';
/** Overheard chatter voices (the ambient rung of the speech budget) — 'off' silences them; default on. */
export const AMBIENT_VOICES_KEY = 'atlas:voices-ambient';
export const VOICES_VOLUME_KEY = 'atlas:voices-volume';

export interface Utterance {
  /** who is speaking (the versona id) */
  speaker: string;
  /** the speech budget's rung (2026-09-15): direct conversation, a warning, or overheard chatter */
  priority: SpeechPriority;
  text: string;
  voice: VoiceChoice;
  chunks: number;
  /** chunks synthesised so far */
  done: number;
  /** ms the worker spent per chunk */
  synthMs: number[];
  seconds: number;
  startedAt: number;
  state: 'queued' | 'speaking' | 'done' | 'stopped' | 'error';
  error?: string;
}

type WorkerMsg =
  | { type: 'ready'; sampleRate: number; voices: string[] }
  | { type: 'audio'; id: number; audio: Float32Array; sampleRate: number; ms: number }
  | { type: 'error'; id: number; error: string };

class VersonaTtsService {
  private worker: Worker | null = null;
  private ready = false;
  private readyWaiters: Array<() => void> = [];
  /** The worker's init failed (model 404, ORT failure): every later speak() reports 'error' instead of queueing forever (2026-09-12). */
  private initFailed = false;
  private failInit(reason: string): void {
    this.initFailed = true;
    this.stats.lastError = reason;
    try { this.worker?.terminate(); } catch { /* gone */ }
    this.worker = null;
    for (const w of this.readyWaiters) w();
    this.readyWaiters = [];
  }
  private nextId = 1;
  private pending = new Map<number, { utter: Utterance; index: number }>();
  private current: Utterance | null = null;
  private gain: GainNode | null = null;
  private playing: AudioBufferSourceNode[] = [];
  /** the time the next chunk may start (chunks play back to back) */
  private tailAt = 0;
  private stats = { spoken: 0, chunks: 0, errors: 0, workerReady: false, lastError: '', voices: [] as string[], loadMs: 0, refused: 0, ambient: 0, ambientRefused: { busy: 0, gap: 0, far: 0, long: 0 } };
  private loadStart = 0;
  readonly history: Utterance[] = [];

  ambientEnabled(): boolean {
    try { return this.enabled() && localStorage.getItem(AMBIENT_VOICES_KEY) !== 'off'; } catch { return this.enabled(); }
  }
  /** The rank of what is live now (0 = silence) — the speech budget's input. */
  liveRank(): number {
    const u = this.current;
    return u && (u.state === 'queued' || u.state === 'speaking') ? PRIORITY_RANK[u.priority] : 0;
  }
  /** When the last overheard line ended (ms, performance.now clock; 0 = never). */
  lastAmbientEndMs(): number {
    for (let i = this.history.length - 1; i >= 0; i--) {
      const u = this.history[i];
      if (u.priority !== 'ambient') continue;
      return u.startedAt + u.seconds * 1000 + u.synthMs.reduce((s, v) => s + v, 0);
    }
    return 0;
  }
  /** May an overheard line be voiced now? */
  ambientVerdict(distM: number, textLen: number): AmbientVerdict {
    return ambientVerdict(this.liveRank(), this.lastAmbientEndMs(), performance.now(), distM, textLen);
  }
  enabled(): boolean {
    try { return localStorage.getItem(VOICES_KEY) !== 'off'; } catch { return true; }
  }
  setEnabled(on: boolean): void {
    try { localStorage.setItem(VOICES_KEY, on ? 'on' : 'off'); } catch { /* private mode */ }
    if (!on) this.stop();
  }
  volume(): number {
    try { const v = Number(localStorage.getItem(VOICES_VOLUME_KEY)); return Number.isFinite(v) && v > 0 ? Math.min(1, v) : 0.9; } catch { return 0.9; }
  }
  setVolume(v: number): void {
    const c = Math.max(0, Math.min(1, v));
    try { localStorage.setItem(VOICES_VOLUME_KEY, String(c)); } catch { /* private mode */ }
    if (this.gain) this.gain.gain.value = c;
  }

  /** Warm the worker (model + WASM) ahead of the first line — call when a talk window opens. */
  prewarm(): void {
    if (!this.enabled()) return;
    void this.ensureWorker();
  }

  private ensureWorker(): Promise<void> {
    if (this.ready || this.initFailed) return Promise.resolve();
    if (!this.worker) {
      this.loadStart = performance.now();
      this.worker = new Worker(new URL('../../workers/tts_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<WorkerMsg>) => this.onMessage(e.data);
      this.worker.onerror = (e) => { this.stats.lastError = String(e.message ?? e); this.stats.errors++; if (!this.ready) this.failInit(String(e.message ?? e)); };
      this.worker.postMessage({ type: 'init', base: document.baseURI });
    }
    return new Promise((r) => this.readyWaiters.push(r));
  }

  private onMessage(m: WorkerMsg): void {
    if (m.type === 'ready') {
      this.ready = true;
      this.stats.workerReady = true;
      this.stats.voices = m.voices;
      this.stats.loadMs = Math.round(performance.now() - this.loadStart);
      for (const w of this.readyWaiters) w();
      this.readyWaiters = [];
      return;
    }
    if (m.type === 'error') {
      this.stats.errors++;
      this.stats.lastError = m.error;
      if (m.id === -1 && !this.ready) { this.failInit(m.error); return; } // the init itself failed: nothing pending can ever resolve
      const p = this.pending.get(m.id);
      if (p) { this.pending.delete(m.id); p.utter.state = 'error'; p.utter.error = m.error; }
      return;
    }
    const p = this.pending.get(m.id);
    this.pending.delete(m.id);
    if (!p) return; // stopped meanwhile
    p.utter.done++;
    p.utter.synthMs.push(m.ms);
    this.stats.chunks++;
    if (p.utter !== this.current || p.utter.state === 'stopped') return;
    this.play(p.utter, m.audio, m.sampleRate);
  }

  private play(utter: Utterance, audio: Float32Array, sampleRate: number): void {
    const out = sfxOutput();
    if (!out || audio.length === 0) return;
    const { ctx, master } = out;
    if (!this.gain) { this.gain = ctx.createGain(); this.gain.gain.value = this.volume(); this.gain.connect(master); }
    const buf = ctx.createBuffer(1, audio.length, sampleRate);
    buf.copyToChannel(audio, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.gain);
    const at = Math.max(ctx.currentTime + 0.02, this.tailAt);
    src.start(at);
    this.tailAt = at + buf.duration;
    // SPEECH DUCKS THE SCORE (2026-09-20): a conversation you opened (or a warning) asks the music and the beds to
    // make room until the last scheduled chunk ends. A LEASE, moved forward by every chunk — `stop()` releases it, so
    // a cancelled line can never leave the world ducked. Overheard chatter does not duck anything: it is background.
    if (utter.priority !== 'ambient') audioRuntime().duck('speech', DUCK_SPEECH, this.tailAt + 0.12);
    utter.seconds += buf.duration;
    utter.state = 'speaking';
    this.playing.push(src);
    src.onended = () => {
      this.playing = this.playing.filter((s) => s !== src);
      if (utter === this.current && utter.done >= utter.chunks && this.playing.length === 0) { utter.state = 'done'; this.stats.spoken++; }
    };
    if (ctx.state === 'suspended') void ctx.resume();
  }

  /** Speak a paragraph in a versona's voice. A direct line replaces whatever plays; a warning
   *  replaces ambient talk; an ambient line is REFUSED while anything is live (speech_budget). */
  speak(speaker: string, text: string, voice: VoiceChoice, priority: SpeechPriority = 'direct'): Utterance | null {
    if (!this.enabled()) return null;
    if (!interruptAllowed(this.liveRank(), priority)) { this.stats.refused++; return null; }
    const chunks = prepareUtterance(text);
    if (chunks.length === 0) return null;
    this.stop();
    if (priority === 'ambient') this.stats.ambient++;
    const utter: Utterance = { speaker, priority, text, voice, chunks: chunks.length, done: 0, synthMs: [], seconds: 0, startedAt: performance.now(), state: 'queued' };
    this.current = utter;
    this.history.push(utter);
    if (this.history.length > 12) this.history.shift();
    const speed = modelSpeed(voice);
    void this.ensureWorker().then(() => {
      if (this.current !== utter || utter.state === 'stopped') return;
      if (!this.worker || !this.ready) { utter.state = 'error'; utter.error = this.stats.lastError ?? 'voice unavailable'; return; }
      chunks.forEach((chunk, index) => {
        const id = this.nextId++;
        this.pending.set(id, { utter, index });
        this.worker!.postMessage({ type: 'synth', id, text: chunk, voice: voice.voice, speed });
      });
    });
    return utter;
  }

  /** Silence the current line (a new line, the window closing, the setting turned off). */
  stop(): void {
    if (this.current && this.current.state !== 'done') this.current.state = 'stopped';
    this.current = null;
    this.pending.clear();
    for (const s of this.playing) { try { s.stop(); } catch { /* already ended */ } }
    this.playing = [];
    this.tailAt = 0;
    audioRuntime().unduck('speech');
  }

  diag(): Record<string, unknown> {
    return {
      enabled: this.enabled(), volume: this.volume(), ...this.stats,
      current: this.current ? { speaker: this.current.speaker, voice: this.current.voice.name, state: this.current.state, chunks: this.current.chunks, done: this.current.done, seconds: +this.current.seconds.toFixed(2), synthMs: this.current.synthMs } : null,
      history: this.history.slice(-5).map((u) => ({ speaker: u.speaker, voice: u.voice.name, state: u.state, seconds: +u.seconds.toFixed(2), text: u.text.slice(0, 60) })),
    };
  }
}

let service: VersonaTtsService | null = null;
/** The one voice service (created on first use; `__tts()` is its diag). */
export function versonaTts(): VersonaTtsService {
  if (!service) service = new VersonaTtsService();
  return service;
}

// Levers registered at module load (the dialogue module imports this at boot), so a probe can
// drive the pipeline from the menu: `__tts()` diag, `__ttsSpeak(text, voice, speed)`, `__ttsStop()`.
if (typeof window !== 'undefined') {
  const w = window as unknown as Record<string, unknown>;
  w.__tts = () => versonaTts().diag();
  w.__ttsSpeak = (text: string, voice = 'expr-voice-3-f', speed = 1) =>
    versonaTts().speak('probe', text, { voice: voice as VoiceChoice['voice'], name: VOICE_NAMES[voice as VoiceChoice['voice']] ?? voice, speed, gender: 'unknown' });
  w.__ttsStop = () => versonaTts().stop();
}
